import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LoadingMorph from 'components/LoadingMorph/LoadingMorph';
import Typography from '@mui/material/Typography';
import { OpenInBrowser, Settings } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { BLOG_DEPLOYMENT_STATE } from '../../../helpers/graphql/hosting';
import Confetti from 'react-confetti';
import useWindowSize from 'react-use/lib/useWindowSize';
import Link from 'next/link';
import graphQLClient from 'helpers/graphQLClient';
import { useRef } from 'react';
import { useSnackbar } from 'notistack';
import { GET_HOSTING_PLAN } from '../../../helpers/graphql/hosting';
import openV2Editor from '../../../helpers/openV2Editor';

export default function BlogCreationProgress({ blogId, editKey }) {
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState(false);
  // v2 only: the blog exists, but its first publish gave up — "create it
  // again" would be wrong advice (the name is taken by this very blog).
  const [publishFailed, setPublishFailed] = useState(false);
  // Which "ready" buttons to show: a v2 blog gets the editor, a v1 blog the
  // editKey site builder. Probed once on READY via getHostingPlan.
  const [isV2, setIsV2] = useState(false);
  // The 3s interval closes over stale state (pre-existing shape), so this ref
  // keeps the one-shot probe from re-firing on every subsequent tick, and the
  // interval id lets a terminal state stop the polling outright.
  const probed = useRef(false);
  const intervalRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  function checkBuildStatus() {
    if (complete || error || publishFailed) return;
    graphQLClient(BLOG_DEPLOYMENT_STATE, { id: blogId })
      .then(data => {
        if (data) {
          const { blogDeploymentState } = data;
          if (blogDeploymentState === 'READY') {
            if (probed.current) return;
            probed.current = true;
            graphQLClient(GET_HOSTING_PLAN)
              .then(res => {
                const blog = res?.getHostingPlan?.blogs?.find(
                  b => b.id === blogId,
                );
                if (!blog) throw new Error('blog missing from hosting plan');
                clearInterval(intervalRef.current);
                setIsV2(blog.hostingV2Status === 'active');
                setComplete(true);
              })
              // A failed lookup must not decide the answer: showing a v2 blog
              // the old site-builder links would be wrong until a reload.
              // Let the next tick ask again.
              .catch(() => {
                probed.current = false;
              });
          } else if (blogDeploymentState === 'ERROR') {
            clearInterval(intervalRef.current);
            setError(true);
          } else if (blogDeploymentState === 'PUBLISH_FAILED') {
            clearInterval(intervalRef.current);
            setPublishFailed(true);
          }
        }
      })
      .catch(err => {
        console.error(err);
      });
  }

  useEffect(() => {
    intervalRef.current = setInterval(checkBuildStatus, 3000);
    return () => clearInterval(intervalRef.current);
  }, []);

  const { width, height } = useWindowSize();

  const handleRefresh = () => {
    // Add a parameter to the current URL
    const currentUrl = window.location.href;
    const separator = currentUrl.includes('?') ? '&' : '?';
    const newUrl = `${currentUrl}${separator}blogId=${blogId}`;

    // Reload the current page with the new URL after a short delay
    setTimeout(() => {
      window.location.href = newUrl;
    }, 100);
  };

  if (complete)
    return (
      <div className="mt-3">
        <Typography variant="h4" className="tw-font-bold" gutterBottom>
          Your blog is ready!
        </Typography>
        <div className="mb-2">
          {isV2
            ? 'Make it your own in the site editor. Just hit the button below.'
            : 'Personalize it even further with our intuitive site builder. Just hit the button below.'}
        </div>
        <Confetti width={width} height={height} recycle={false} />
        <div className="row">
          <div className="col-6 p-1">
            <a
              href={
                isV2
                  ? `https://${blogId}.truvvl.blog/`
                  : `https://${blogId}.truvvl.blog/api/preview`
              }
              target="_blank"
              rel="nofollow noreferrer noopener"
              onClick={handleRefresh}
            >
              <Button fullWidth variant="contained" color="primary">
                Open Blog <OpenInBrowser className="ml-2" />
              </Button>
            </a>
          </div>
          <div className="col-6 p-1">
            {isV2 ? (
              <Button
                fullWidth
                variant="outlined"
                color="primary"
                onClick={() => {
                  // Resolves false when the backend refuses to mint a session
                  // — saying so beats a button that does nothing.
                  openV2Editor(blogId)
                    .then(opened => {
                      if (!opened)
                        enqueueSnackbar(
                          'Your editor is still starting up — try again in a moment.',
                          { variant: 'info' },
                        );
                    })
                    .catch(err => {
                      console.error(err);
                      enqueueSnackbar('Could not open the editor. Please try again.', {
                        variant: 'error',
                      });
                    });
                }}
              >
                Open Editor <Settings className="ml-2" />
              </Button>
            ) : (
              <a
                href={`https://${blogId}.truvvl.blog/api/edit?editKey=${editKey}`}
                target="_blank"
                rel="nofollow noreferrer noopener"
                onClick={handleRefresh}
              >
                <Button fullWidth variant="outlined" color="primary">
                  Open Site Builder <Settings className="ml-2" />
                </Button>
              </a>
            )}
          </div>
        </div>
      </div>
    );
  if (publishFailed)
    return (
      <div className="mt-3">
        <Typography variant="h4" gutterBottom className="tw-font-bold">
          Almost there…
        </Typography>
        <span className="highlightLinks">
          Your blog was created, but it could not go online yet. Your blog and
          its settings are saved — please{' '}
          <Link href="/about/contact?support=true">contact support</Link> and we
          will get it online for you.
        </span>
      </div>
    );
  if (error)
    return (
      <div className="mt-3">
        <Typography variant="h4" gutterBottom className="tw-font-bold">
          Something went wrong...
        </Typography>
        <span className="highlightLinks">
          Sorry about that. Please try creating the blog again and{' '}
          <Link href="/about/contact?support=true">contact support</Link> if the
          issue persists.
        </span>
      </div>
    );
  return (
    <Box display="flex" alignItems="center">
      <Box width="100%" mr={1}>
        <LoadingMorph />
      </Box>
    </Box>
  );
}
